import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Lock, Shield, KeyRound, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import {
  resetPasswordSchema,
  type ResetPasswordFormData,
} from "@/schemas/authSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { useResetPassword } from "@/hooks/useAuth";
import PrefLogo from "@/assets/logo_municipal.svg";

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetPassword = useResetPassword();

  const token = searchParams.get("token");

  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    await resetPassword.mutateAsync(
      {
        token: token as string,
        password: data.password,
        password_confirmation: data.confirmPassword,
      },
      {
        onSuccess: () => {
          toast.success("Senha redefinida com sucesso!");
          navigate("/login");
        },
        onError: () => {
          toast.error("Erro ao redefinir a senha. Por favor, tente novamente.");
        },
      },
    );
  };

  if (!token) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))]/20 via-[hsl(var(--primary))]/5 to-[hsl(var(--secondary))]/10">
        {/* Elementos decorativos de fundo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute -top-24 -left-24 w-96 h-96 bg-[hsl(var(--primary))]/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              rotate: [0, -90, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute -bottom-24 -right-24 w-96 h-96 bg-[hsl(var(--secondary))]/10 rounded-full blur-3xl"
          />
          <div className="absolute inset-0 opacity-20">
            <div className="h-full w-full bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:20px_20px]" />
          </div>
        </div>

        {/* Container principal */}
        <div className="w-full max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center relative z-10">
          {/* Coluna esquerda - Informações */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="hidden md:block"
          >
            <div className="space-y-8">
              <div>
                <div className="mb-2">
                  <p className="text-sm font-semibold text-[hsl(var(--primary))] uppercase tracking-wider mb-2">
                    
                  </p>
                  <h1 className="text-6xl font-black mb-2 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] bg-clip-text text-transparent">
                    Vida+Simples
                  </h1>
                  <p className="text-lg font-medium text-gray-700">Ceará</p>
                </div>
                <p className="text-xl text-gray-600 leading-relaxed mt-6">
                  Redefina sua senha de forma rápida e segura
                </p>
              </div>

              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
                >
                  <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                    <Shield className="h-6 w-6 text-[hsl(var(--primary))]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Proteção de Dados
                    </h3>
                    <p className="text-sm text-gray-600">
                      Suas informações são protegidas com criptografia
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
                >
                  <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                    <KeyRound className="h-6 w-6 text-[hsl(var(--primary))]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Processo Seguro
                    </h3>
                    <p className="text-sm text-gray-600">
                      Redefinição de senha com validação em duas etapas
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Coluna direita - Card de erro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full"
          >
            <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
              <CardHeader className="space-y-4 pb-8">
                <div className="flex items-center justify-center mb-2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  >
                    <img
                      src={PrefLogo}
                      alt="Logo Municipal"
                      className="h-25 w-25"
                    />
                  </motion.div>
                </div>
                <div className="flex justify-center mb-2">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-10 w-10 text-red-600" />
                  </div>
                </div>
                <CardTitle className="text-3xl font-bold text-center text-gray-900">
                  Link Inválido
                </CardTitle>
                <p className="text-center text-muted-foreground text-sm">
                  O link de recuperação é inválido ou expirou
                </p>
              </CardHeader>

              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-800 text-center">
                      Por favor, solicite um novo link de recuperação de senha.
                    </p>
                  </div>

                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={() => navigate("/recovery-password")}
                      className="w-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--secondary))] hover:to-[hsl(var(--primary))]"
                    >
                      Solicitar novo link
                    </Button>
                    <Button
                      onClick={() => navigate("/login")}
                      variant="outline"
                      className="w-full"
                    >
                      Voltar para Login
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))]/20 via-[hsl(var(--primary))]/5 to-[hsl(var(--secondary))]/10">
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-24 -left-24 w-96 h-96 bg-[hsl(var(--primary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -90, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -bottom-24 -right-24 w-96 h-96 bg-[hsl(var(--secondary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, -20, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/2 left-1/4 w-64 h-64 bg-[hsl(var(--primary))]/5 rounded-full blur-2xl"
        />
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:20px_20px]" />
        </div>
      </div>

      {/* Container principal com duas colunas */}
      <div className="w-full max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Coluna esquerda - Informações */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden md:block"
        >
          <div className="space-y-8">
            <div>
              <div className="mb-2">
                <p className="text-sm font-semibold text-[hsl(var(--primary))] uppercase tracking-wider mb-2">
                  
                </p>
                <h1 className="text-6xl font-black mb-2 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] bg-clip-text text-transparent">
                  Vida+Simples
                </h1>
                <p className="text-lg font-medium text-gray-700">Ceará</p>
              </div>
              <p className="text-xl text-gray-600 leading-relaxed mt-6">
                Redefina sua senha de forma rápida e segura
              </p>
            </div>

            {/* Cards de features */}
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Shield className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Proteção de Dados
                  </h3>
                  <p className="text-sm text-gray-600">
                    Suas informações são protegidas com criptografia
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <KeyRound className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Processo Seguro
                  </h3>
                  <p className="text-sm text-gray-600">
                    Redefinição de senha com validação em duas etapas
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Lock className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Senha Forte
                  </h3>
                  <p className="text-sm text-gray-600">
                    Crie uma senha segura seguindo nossos requisitos
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Coluna direita - Formulário */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full"
        >
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="space-y-4 pb-8">
              <div className="flex items-center justify-center mb-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                >
                  <img
                    src={PrefLogo}
                    alt="Logo Municipal"
                    className="h-25 w-25"
                  />
                </motion.div>
              </div>
              <CardTitle className="text-3xl font-bold text-center text-gray-900">
                Criar Nova Senha
              </CardTitle>
              <p className="text-center text-muted-foreground text-sm">
                Digite sua nova senha abaixo
              </p>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Nova Senha
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      {...registerField("password")}
                      id="password"
                      placeholder="••••••••"
                      showPasswordToggle
                      className={`pl-10 pr-4 focus-visible:ring-[hsl(var(--primary))] ${errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      error={errors.password?.message}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="confirmPassword"
                    className="text-sm font-medium"
                  >
                    Confirmar Nova Senha
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      {...registerField("confirmPassword")}
                      id="confirmPassword"
                      placeholder="••••••••"
                      showPasswordToggle
                      className={`pl-10 pr-4 focus-visible:ring-[hsl(var(--primary))] ${errors.confirmPassword ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      error={errors.confirmPassword?.message}
                    />
                  </div>
                </div>

                <div className="p-3 bg-[hsl(var(--primary))]/5 rounded-lg border border-[hsl(var(--primary))]/20">
                  <p className="text-xs text-gray-700">
                    A senha deve conter no mínimo 8 caracteres, incluindo: letra
                    maiúscula, letra minúscula, número e caractere especial
                    (@$!%*?&#)
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--secondary))] hover:to-[hsl(var(--primary))] shadow-lg"
                  isLoading={resetPassword.isPending}
                >
                  Redefinir Senha
                </Button>

                <div className="text-center pt-2">
                  <Link
                    to="/login"
                    className="text-sm text-[hsl(var(--primary))] font-semibold hover:underline"
                  >
                    Voltar para Login
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
